import React, { useState, useEffect, useMemo, useRef } from "react"
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Select, MenuItem, FormControl,
    InputLabel, Grid, Collapse, Chip, Checkbox, CircularProgress
} from "@mui/material"
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Trash2, Upload, FileSpreadsheet, ChevronDown } from "lucide-react"
import { toastSuccess, toastError } from "../../utils"
import ConfirmModal from "../Common/ConfirmModal"
import CustomShowModal from "../Common/customShowModal"
import ActionButton from "../../components/ActionButton"
import * as actions from "./actions"
import { format, addDays } from "date-fns"

const STORE_OPTIONS = [
    { value: "spotify", label: "Spotify" },
    { value: "apple_music", label: "Apple Music" },
    { value: "amazon_music", label: "Amazon Music" },
    { value: "deezer", label: "Deezer" },
    { value: "pandora", label: "Pandora" },
    { value: "soundcloud", label: "SoundCloud" },
    { value: "youtube", label: "YouTube" },
    { value: "youtube_content_id", label: "YouTube Content ID" },
    { value: "youtube_shorts", label: "YouTube Shorts" },
]

function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

function parseDDMMYYYY(dateStr) {
    const [dd, mm, yyyy] = dateStr.split('-')
    return new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd))
}

function formatDateLabel(startDate, endDate) {
    const start = parseDDMMYYYY(startDate)
    const end = parseDDMMYYYY(endDate)
    return `${format(start, 'd MMM yyyy')} - ${format(end, 'd MMM yyyy')}`
}

function groupFilesByMonth(files) {
    const groups = {}
    files.forEach((file) => {
        const date = parseDDMMYYYY(file.startDate)
        const label = format(date, 'MMMM yyyy')
        if (!groups[label]) {
            groups[label] = { label, sortDate: date, files: [] }
        }
        groups[label].files.push(file)
    })
    return Object.values(groups).sort((a, b) => b.sortDate - a.sortDate)
}

export default function AnalyticsUpload(props) {
    const [files, setFiles] = useState([])
    const [store, setStore] = useState("spotify")
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [deleteFilename, setDeleteFilename] = useState(null)
    const [selectedYear, setSelectedYear] = useState(null)
    const [expandedMonths, setExpandedMonths] = useState({})
    const [filterStore, setFilterStore] = useState("")

    // Bulk delete state
    const [selectedFiles, setSelectedFiles] = useState(new Set())
    const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
    const [bulkDeleting, setBulkDeleting] = useState(false)
    const [bulkDeleteDone, setBulkDeleteDone] = useState(false)
    const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 })
    const [bulkDeleteCurrent, setBulkDeleteCurrent] = useState('')
    const [bulkDeleteResults, setBulkDeleteResults] = useState([])
    const [bulkDeleteCountdown, setBulkDeleteCountdown] = useState(0)
    const bulkDeleteCancelRef = useRef(false)
    const bulkDeleteEndTimeRef = useRef(0)
    const bulkDeleteCountdownRef = useRef(null)

    useEffect(() => {
        fetchFiles()
    }, [])

    // Lock tab during bulk delete
    useEffect(() => {
        if (!bulkDeleting) return
        const handler = (e) => {
            e.preventDefault()
            e.returnValue = 'Bulk delete is in progress. Your current progress will be lost if you leave.'
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [bulkDeleting])

    const availableStores = useMemo(() => {
        const stores = new Set()
        files.forEach((file) => stores.add(file.store))
        return stores
    }, [files])

    const storeFilteredFiles = useMemo(() => {
        if (!filterStore) return files
        return files.filter((file) => file.store === filterStore)
    }, [files, filterStore])

    const groupedFiles = useMemo(() => groupFilesByMonth(storeFilteredFiles), [storeFilteredFiles])

    const availableYears = useMemo(() => {
        const years = new Set()
        storeFilteredFiles.forEach((file) => {
            years.add(parseDDMMYYYY(file.startDate).getFullYear())
        })
        return [...years].sort((a, b) => b - a)
    }, [storeFilteredFiles])

    const filteredGroups = useMemo(() => {
        if (!selectedYear) return groupedFiles
        return groupedFiles.filter((g) => g.sortDate.getFullYear() === selectedYear)
    }, [groupedFiles, selectedYear])

    useEffect(() => {
        if (availableYears.length > 0 && !selectedYear) {
            setSelectedYear(availableYears[0])
        }
    }, [availableYears])

    useEffect(() => {
        if (selectedYear) {
            const newExpanded = {}
            groupedFiles
                .filter((g) => g.sortDate.getFullYear() === selectedYear)
                .forEach((g) => { newExpanded[g.label] = true })
            setExpandedMonths(newExpanded)
        }
    }, [selectedYear, groupedFiles])

    const fetchFiles = () => {
        if (props.setLoader) props.setLoader(true)
        actions.getAnalysisFilesAdmin()
            .then((res) => {
                setFiles(Array.isArray(res.body) ? res.body : [])
                if (props.setLoader) props.setLoader(false)
            })
            .catch((err) => {
                toastError(err?.message || "Failed to load files")
                if (props.setLoader) props.setLoader(false)
            })
    }

    const handleFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            if (!file.name.toLowerCase().endsWith('.xlsx')) {
                toastError("Only .xlsx files are allowed")
                return
            }
            setSelectedFile(file)
        }
    }

    const handleUpload = () => {
        if (!selectedFile) {
            toastError("Please select a file")
            return
        }
        if (!startDate || !endDate) {
            toastError("Please select start and end dates")
            return
        }

        const formData = new FormData()
        formData.append("file", selectedFile)
        formData.append("store", store)
        formData.append("startDate", format(startDate, 'dd-MM-yyyy'))
        formData.append("endDate", format(endDate, 'dd-MM-yyyy'))

        setUploading(true)
        if (props.setLoader) props.setLoader(true)
        actions.uploadAnalysisFile(formData)
            .then((res) => {
                toastSuccess(res.message || "File uploaded successfully")
                setSelectedFile(null)
                setStartDate(null)
                setEndDate(null)
                // Reset file input
                const fileInput = document.getElementById('analysis-file-input')
                if (fileInput) fileInput.value = ''
                fetchFiles()
                setUploading(false)
                if (props.setLoader) props.setLoader(false)
            })
            .catch((err) => {
                toastError(err?.message || "Upload failed")
                setUploading(false)
                if (props.setLoader) props.setLoader(false)
            })
    }

    const handleDeleteClick = (filename) => {
        setDeleteFilename(filename)
        setConfirmOpen(true)
    }

    const handleDeleteConfirm = () => {
        if (!deleteFilename) return
        if (props.setLoader) props.setLoader(true)
        actions.deleteAnalysisFileAdmin({ filename: deleteFilename })
            .then((res) => {
                toastSuccess(res.message || "File deleted")
                setConfirmOpen(false)
                setDeleteFilename(null)
                fetchFiles()
                if (props.setLoader) props.setLoader(false)
            })
            .catch((err) => {
                toastError(err?.message || "Delete failed")
                setConfirmOpen(false)
                if (props.setLoader) props.setLoader(false)
            })
    }

    const handleYearChange = (e) => {
        const year = e.target.value
        setSelectedYear(year)
        const newExpanded = {}
        groupedFiles
            .filter((g) => g.sortDate.getFullYear() === year)
            .forEach((g) => { newExpanded[g.label] = true })
        setExpandedMonths(newExpanded)
    }

    const toggleMonth = (label) => {
        setExpandedMonths((prev) => ({ ...prev, [label]: !prev[label] }))
    }

    // Checkbox helpers
    const toggleFileSelection = (filename) => {
        setSelectedFiles((prev) => {
            const next = new Set(prev)
            if (next.has(filename)) next.delete(filename)
            else next.add(filename)
            return next
        })
    }

    const toggleMonthSelection = (group) => {
        const filenames = group.files.map((f) => f.filename)
        const allSelected = filenames.every((f) => selectedFiles.has(f))
        setSelectedFiles((prev) => {
            const next = new Set(prev)
            filenames.forEach((f) => {
                if (allSelected) next.delete(f)
                else next.add(f)
            })
            return next
        })
    }

    const isMonthAllSelected = (group) => {
        return group.files.length > 0 && group.files.every((f) => selectedFiles.has(f.filename))
    }

    const isMonthPartialSelected = (group) => {
        const some = group.files.some((f) => selectedFiles.has(f.filename))
        return some && !isMonthAllSelected(group)
    }

    // Bulk delete
    const handleBulkDelete = async () => {
        const filenames = [...selectedFiles]
        if (filenames.length === 0) return

        bulkDeleteCancelRef.current = false
        setBulkDeleteOpen(true)
        setBulkDeleting(true)
        setBulkDeleteDone(false)
        setBulkDeleteResults([])
        setBulkDeleteProgress({ current: 0, total: filenames.length })
        bulkDeleteEndTimeRef.current = Date.now() + filenames.length * 7 * 1000
        setBulkDeleteCountdown(filenames.length * 7)

        clearInterval(bulkDeleteCountdownRef.current)
        bulkDeleteCountdownRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((bulkDeleteEndTimeRef.current - Date.now()) / 1000))
            setBulkDeleteCountdown(remaining)
        }, 1000)

        const results = []
        for (let i = 0; i < filenames.length; i++) {
            if (bulkDeleteCancelRef.current) break
            const filename = filenames[i]
            setBulkDeleteCurrent(filename)
            setBulkDeleteProgress({ current: i + 1, total: filenames.length })
            bulkDeleteEndTimeRef.current = Date.now() + (filenames.length - i) * 7 * 1000

            try {
                const res = await actions.deleteAnalysisFileAdmin({ filename })
                if (res.status === 200) {
                    results.push({ filename, status: 'success' })
                } else {
                    results.push({ filename, status: 'failed', message: res.message || 'Delete failed' })
                }
            } catch (err) {
                results.push({ filename, status: 'failed', message: err?.message || 'Request failed' })
            }

            setBulkDeleteResults([...results])

            if (i < filenames.length - 1 && !bulkDeleteCancelRef.current) {
                await new Promise((r) => setTimeout(r, 7000))
            }
        }

        clearInterval(bulkDeleteCountdownRef.current)
        bulkDeleteCountdownRef.current = null
        setBulkDeleting(false)
        setBulkDeleteDone(true)
        setBulkDeleteCurrent('')
        setBulkDeleteCountdown(0)
        setSelectedFiles(new Set())
        fetchFiles()
    }

    const handleBulkDeleteCancel = () => {
        bulkDeleteCancelRef.current = true
        clearInterval(bulkDeleteCountdownRef.current)
        bulkDeleteCountdownRef.current = null
    }

    const handleCloseBulkDelete = () => {
        if (bulkDeleting) return
        setBulkDeleteOpen(false)
        setBulkDeleteResults([])
        setBulkDeleteDone(false)
        setBulkDeleteProgress({ current: 0, total: 0 })
        setBulkDeleteCurrent('')
        setBulkDeleteCountdown(0)
    }

    const totalFilteredFiles = filteredGroups.reduce((sum, g) => sum + g.files.length, 0)

    return (
        <Box sx={{ p: 3 }}>
            {/* Upload Section */}
            <Paper sx={{
                p: 3, mb: 4, backgroundColor: '#1E2030', borderRadius: '12px',
                border: '1px solid #2E3247'
            }}>
                <Typography sx={{ fontWeight: 600, color: '#E2E8F0', mb: 2, fontSize: '1rem' }}>
                    Upload New Analytics File
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel sx={{ color: '#94A3B8' }}>Store</InputLabel>
                        <Select
                            value={store}
                            onChange={(e) => setStore(e.target.value)}
                            label="Store"
                            sx={{
                                color: '#E2E8F0',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3E4257' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                '.MuiSvgIcon-root': { color: '#94A3B8' },
                            }}
                        >
                            {STORE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={(date) => {
                                setStartDate(date)
                                if (endDate && date && endDate <= date) setEndDate(null)
                            }}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    sx: {
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            color: '#E2E8F0',
                                            '& fieldset': { borderColor: '#3E4257' },
                                            '&:hover fieldset': { borderColor: '#6C63FF' },
                                            '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
                                        },
                                        '& .MuiInputLabel-root': { color: '#94A3B8' },
                                        '& .MuiSvgIcon-root': { color: '#94A3B8' },
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={(date) => {
                                if (startDate && date && date <= startDate) return
                                setEndDate(date)
                            }}
                            minDate={startDate ? addDays(startDate, 1) : undefined}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    fullWidth: true,
                                    sx: {
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            color: '#E2E8F0',
                                            '& fieldset': { borderColor: '#3E4257' },
                                            '&:hover fieldset': { borderColor: '#6C63FF' },
                                            '&.Mui-focused fieldset': { borderColor: '#6C63FF' },
                                        },
                                        '& .MuiInputLabel-root': { color: '#94A3B8' },
                                        '& .MuiSvgIcon-root': { color: '#94A3B8' },
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                    <Button
                        component="label"
                        startIcon={<FileSpreadsheet size={16} />}
                        sx={{
                            color: selectedFile ? '#E2E8F0' : '#94A3B8',
                            textTransform: 'none',
                            border: '1px dashed',
                            borderColor: selectedFile ? '#6C63FF' : '#3E4257',
                            borderRadius: '8px',
                            px: 2,
                            flex: 1,
                            backgroundColor: selectedFile ? 'rgba(108, 99, 255, 0.08)' : 'transparent',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover': { borderColor: '#6C63FF', backgroundColor: 'rgba(108, 99, 255, 0.08)', color: '#E2E8F0' },
                        }}
                    >
                        {selectedFile ? selectedFile.name : "Select File"}
                        <input
                            id="analysis-file-input"
                            type="file"
                            hidden
                            accept=".xlsx"
                            onChange={handleFileSelect}
                        />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={uploading || !selectedFile || !startDate || !endDate}
                        startIcon={<Upload size={16} />}
                        sx={{
                            backgroundColor: '#6C63FF',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: '#5B54E0' },
                            '&.Mui-disabled': { backgroundColor: '#3E4257', color: '#64748B' },
                        }}
                    >
                        {uploading ? "Uploading..." : "Upload"}
                    </Button>
                </Box>
                {selectedFile && startDate && endDate && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 1.5 }}>
                        Will save as: <span style={{ color: '#6C63FF' }}>{`${store}_${format(startDate, 'dd-MM-yyyy')}_to_${format(endDate, 'dd-MM-yyyy')}.xlsx`}</span>
                    </Typography>
                )}
            </Paper>

            {/* Store filter + Year filter + heading */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, color: '#E2E8F0', fontSize: '1rem' }}>
                        Uploaded Files <span style={{ color: '#6C63FF', fontWeight: 700 }}>({totalFilteredFiles})</span>
                    </Typography>
                    {selectedFiles.size > 0 && (
                        <ActionButton variant="danger" onClick={() => setBulkDeleteConfirmOpen(true)} sx={{ fontSize: '0.8rem', py: 0.4, px: 1.5, gap: 0.5 }}>
                            <Trash2 size={14} /> Delete Selected ({selectedFiles.size})
                        </ActionButton>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                            value={filterStore}
                            onChange={(e) => setFilterStore(e.target.value)}
                            displayEmpty
                            sx={{
                                color: '#E2E8F0',
                                fontSize: '0.875rem',
                                '.MuiOutlinedInput-notchedOutline': { borderColor: '#3E4257' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                '.MuiSvgIcon-root': { color: '#94A3B8' },
                            }}
                        >
                            <MenuItem value="">All Stores</MenuItem>
                            {STORE_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value} disabled={!availableStores.has(opt.value)}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {availableYears.length > 1 && (
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                                value={selectedYear || ''}
                                onChange={handleYearChange}
                                sx={{
                                    color: '#E2E8F0',
                                    fontSize: '0.875rem',
                                    '.MuiOutlinedInput-notchedOutline': { borderColor: '#3E4257' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6C63FF' },
                                    '.MuiSvgIcon-root': { color: '#94A3B8' },
                                }}
                            >
                                {availableYears.map((year) => (
                                    <MenuItem key={year} value={year}>{year}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {availableYears.length === 1 && (
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                            {availableYears[0]}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Grouped files by month */}
            {filteredGroups.length === 0 ? (
                <Paper sx={{
                    backgroundColor: '#1E2030', borderRadius: '12px',
                    border: '1px solid #2E3247', p: 4, textAlign: 'center'
                }}>
                    <Typography sx={{ color: '#64748B' }}>
                        No analytics files uploaded yet.
                    </Typography>
                </Paper>
            ) : (
                filteredGroups.map((group) => (
                    <Paper key={group.label} sx={{
                        backgroundColor: '#1E2030', borderRadius: '12px',
                        border: '1px solid #2E3247', mb: 2, overflow: 'hidden'
                    }}>
                        <Box
                            sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: 2, py: 1.5, cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(108, 99, 255, 0.05)' },
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Checkbox
                                    size="small"
                                    checked={isMonthAllSelected(group)}
                                    indeterminate={isMonthPartialSelected(group)}
                                    onChange={(e) => { e.stopPropagation(); toggleMonthSelection(group) }}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ color: '#64748B', '&.Mui-checked': { color: '#6C63FF' }, '&.MuiCheckbox-indeterminate': { color: '#6C63FF' }, p: 0.5 }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={() => toggleMonth(group.label)}>
                                    <ChevronDown
                                        size={18}
                                        style={{
                                            color: '#94A3B8',
                                            transform: expandedMonths[group.label] ? 'rotate(0deg)' : 'rotate(-90deg)',
                                            transition: 'transform 0.2s',
                                        }}
                                    />
                                    <Typography sx={{ fontWeight: 600, color: '#E2E8F0', fontSize: '0.9rem' }}>
                                        {group.label}
                                    </Typography>
                                </Box>
                            </Box>
                            <Chip
                                label={`${group.files.length} file${group.files.length !== 1 ? 's' : ''}`}
                                size="small"
                                onClick={() => toggleMonth(group.label)}
                                sx={{
                                    backgroundColor: 'rgba(108, 99, 255, 0.15)',
                                    color: '#6C63FF',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24,
                                }}
                            />
                        </Box>
                        <Collapse in={!!expandedMonths[group.label]}>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ '& th': { color: '#94A3B8', fontWeight: 600, borderBottom: '1px solid #2E3247', fontSize: '0.8rem' } }}>
                                            <TableCell padding="checkbox" sx={{ width: 40 }} />
                                            <TableCell>Store</TableCell>
                                            <TableCell>Date Range</TableCell>
                                            <TableCell>Filename</TableCell>
                                            <TableCell>Size</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {group.files.map((file) => (
                                            <TableRow key={file.filename} sx={{ '& td': { color: '#E2E8F0', borderBottom: '1px solid #2E3247' } }}>
                                                <TableCell padding="checkbox" sx={{ width: 40 }}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={selectedFiles.has(file.filename)}
                                                        onChange={() => toggleFileSelection(file.filename)}
                                                        sx={{ color: '#64748B', '&.Mui-checked': { color: '#6C63FF' }, p: 0.5 }}
                                                    />
                                                </TableCell>
                                                <TableCell>{STORE_OPTIONS.find(o => o.value === file.store)?.label || file.store}</TableCell>
                                                <TableCell>{formatDateLabel(file.startDate, file.endDate)}</TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: '#94A3B8' }}>{file.filename}</TableCell>
                                                <TableCell>{formatFileSize(file.size)}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDeleteClick(file.filename)}
                                                        sx={{ color: '#EF4444', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)' } }}
                                                    >
                                                        <Trash2 size={16} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </Paper>
                ))
            )}

            <ConfirmModal
                open={confirmOpen}
                handleClose={() => { setConfirmOpen(false); setDeleteFilename(null); }}
                onSubmitConfirm={handleDeleteConfirm}
                title="Delete File"
                message={`Are you sure you want to delete "${deleteFilename}"? This action cannot be undone.`}
            />

            <ConfirmModal
                open={bulkDeleteConfirmOpen}
                handleClose={() => setBulkDeleteConfirmOpen(false)}
                onSubmitConfirm={() => { setBulkDeleteConfirmOpen(false); handleBulkDelete(); }}
                title="Bulk Delete Files"
                message={`Are you sure you want to delete ${selectedFiles.size} file${selectedFiles.size !== 1 ? 's' : ''}? This action cannot be undone.`}
            />

            {/* Bulk Delete Progress Modal */}
            <CustomShowModal
                open={bulkDeleteOpen}
                handleClose={handleCloseBulkDelete}
                title={bulkDeleteDone ? "Bulk Delete Complete" : `Deleting Files (${bulkDeleteProgress.current}/${bulkDeleteProgress.total})`}
            >
                <Box sx={{ overflow: 'hidden' }}>
                    {bulkDeleting && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                <CircularProgress size={20} sx={{ color: '#6C63FF' }} />
                                <Typography sx={{ color: '#E2E8F0', fontSize: '0.9rem' }}>
                                    Deleting: <span style={{ color: '#6C63FF' }}>{bulkDeleteCurrent}</span>
                                </Typography>
                            </Box>
                            <Box sx={{ width: '100%', backgroundColor: '#2E3247', borderRadius: '6px', height: 8, mb: 1.5 }}>
                                <Box sx={{
                                    width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                                    backgroundColor: '#6C63FF', borderRadius: '6px', height: '100%',
                                    transition: 'width 0.3s ease'
                                }} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                                    {bulkDeleteProgress.current} of {bulkDeleteProgress.total} files
                                </Typography>
                                <Typography sx={{ color: '#94A3B8', fontSize: '0.8rem' }}>
                                    Est. remaining: {Math.floor(bulkDeleteCountdown / 60) > 0 ? `${Math.floor(bulkDeleteCountdown / 60)}m ` : ''}{bulkDeleteCountdown % 60}s
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                <ActionButton variant="danger" onClick={handleBulkDeleteCancel} sx={{ fontSize: '0.8rem', py: 0.4, px: 1.5 }}>
                                    Cancel
                                </ActionButton>
                            </Box>
                        </>
                    )}

                    {bulkDeleteDone && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ color: '#E2E8F0', fontSize: '0.95rem', fontWeight: 600, mb: 1 }}>
                                Summary
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                                <Typography sx={{ color: '#22C55E', fontSize: '0.85rem' }}>
                                    Success: {bulkDeleteResults.filter(r => r.status === 'success').length}
                                </Typography>
                                <Typography sx={{ color: '#EF4444', fontSize: '0.85rem' }}>
                                    Failed: {bulkDeleteResults.filter(r => r.status === 'failed').length}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {bulkDeleteResults.length > 0 && (
                        <Box sx={{ maxHeight: 250, overflowY: 'auto', border: '1px solid #2E3247', borderRadius: '8px', p: 1 }}>
                            {bulkDeleteResults.map((r, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: idx < bulkDeleteResults.length - 1 ? '1px solid #2E3247' : 'none' }}>
                                    <Typography sx={{ color: r.status === 'success' ? '#22C55E' : '#EF4444', fontSize: '0.8rem', fontWeight: 600, minWidth: 14 }}>
                                        {r.status === 'success' ? '✓' : '✗'}
                                    </Typography>
                                    <Typography sx={{ color: '#CBD5E1', fontSize: '0.8rem', flex: 1, wordBreak: 'break-all' }}>
                                        {r.filename}
                                    </Typography>
                                    {r.message && (
                                        <Typography sx={{ color: '#EF4444', fontSize: '0.75rem' }}>
                                            {r.message}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </CustomShowModal>
        </Box>
    )
}
